import {ActionWithPayload} from '../../../../../helpers/reduxHelpers';
import {AccountModel, ApiPreviewAccounts} from '../../../../../models/api/accountModel';
import {ApiPreviewCards, CardModel} from '../../../../../models/api/cardModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../models/api/crmSyncModel';
import {postApiAsync} from '../../../../../services/http/http';
import {findPanelSequence} from '../../../../../services/panels';
import {buildPanelCancelToken} from '../../../../../services/panelsCancellers';
import {ThunkAction, ThunkAsyncAction} from '../../../../appThunk';
import {getCompanyUrl} from '../../../../data/company/companySelectors';
import {setPanelAction} from '../../../panelsActions';
import {makeGetAppPanelIdFromPanel} from '../../../panelsSelectors';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  ContactsManagerPanelId,
  CrmPreviewPanelId,
  makeCrmPreviewPanelId,
  PanelTypesEnum,
  SettingsPanelId,
} from '../../../panelStateBase';
import {makeGetCrmPreviewPanel} from './crmPreviewSelectors';

/*
 * Type enum.
 */

export enum CrmPreviewActionsEnum {
  FETCH_PREVIEW_DATA = '@@panels/crm-preview/FETCH_PREVIEW_DATA',
  FETCH_PREVIEW_DATA_COMPLETED = '@@panels/crm-preview/FETCH_PREVIEW_DATA_COMPLETED',
  FETCH_PREVIEW_DATA_FAILED = '@@panels/crm-preview/FETCH_PREVIEW_DATA_FAILED',
  UPDATE_SELECTED_ID = '@@panels/crm-preview/UPDATE_SELECTED_ID',
}

/*
 * Types.
 */

interface CrmPreviewGenericPayload {
  panelId: CrmPreviewPanelId;
}

interface FetchPreviewDataAction extends ActionWithPayload<CrmPreviewGenericPayload> {
  type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA;
}

interface FetchPreviewDataCompletedActionPayload extends CrmPreviewGenericPayload {
  accounts: ReadonlyArray<AccountModel>;
  cards: ReadonlyArray<CardModel>;
}

interface FetchPreviewDataCompletedAction extends ActionWithPayload<FetchPreviewDataCompletedActionPayload> {
  type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA_COMPLETED;
}

interface FetchPreviewDataFailedAction extends ActionWithPayload<CrmPreviewGenericPayload> {
  type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA_FAILED;
}

interface UpdateSelectedIdActionPayload extends CrmPreviewGenericPayload {
  selectedId: number;
}

interface UpdateSelectedIdAction extends ActionWithPayload<UpdateSelectedIdActionPayload> {
  type: CrmPreviewActionsEnum.UPDATE_SELECTED_ID;
}

/*
 * Action Creator.
 */

export function fetchPreviewDataAction(panelId: CrmPreviewPanelId): FetchPreviewDataAction {
  return {
    type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA,
    payload: {
      panelId,
    },
  };
}

export function fetchPreviewDataCompletedAction(
  panelId: CrmPreviewPanelId,
  accounts: ReadonlyArray<AccountModel>,
  cards: ReadonlyArray<CardModel>,
): FetchPreviewDataCompletedAction {
  return {
    type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA_COMPLETED,
    payload: {
      panelId,
      accounts,
      cards,
    },
  };
}

export function fetchPreviewDataFailedAction(panelId: CrmPreviewPanelId): FetchPreviewDataFailedAction {
  return {
    type: CrmPreviewActionsEnum.FETCH_PREVIEW_DATA_FAILED,
    payload: {
      panelId,
    },
  };
}

export function updateSelectedCrmPreviewAction(
  panelId: CrmPreviewPanelId,
  selectedId: number,
): UpdateSelectedIdAction {
  return {
    type: CrmPreviewActionsEnum.UPDATE_SELECTED_ID,
    payload: {
      panelId,
      selectedId,
    },
  };
}

/*
 * Union Type.
 */

export type CrmPreviewAction =
  | FetchPreviewDataAction
  | FetchPreviewDataCompletedAction
  | FetchPreviewDataFailedAction
  | UpdateSelectedIdAction;

export type CrmPreviewParentPanelId =
  | ContactsManagerPanelId
  | AccountDetailsPanelId
  | SettingsPanelId
  | ContactDetailsPanelId;

export type CrmPreviewMapping = {
  [T: string]: number;
};

/*
 * Actions.
 */

export function popoutCrmPreviewAction(
  parentPanelId: CrmPreviewParentPanelId,
  crmSyncId: number,
  crmSyncSource: CrmSyncSourcesEnum,
  crmSyncResourceType: CrmSyncIntegrationResourceTypesEnum,
  mapping: CrmPreviewMapping,
  externalListId?: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panelId = makeCrmPreviewPanelId(parentPanelId);
    const popupAppPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    const [cancelTokenId] = buildPanelCancelToken(panelId);

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.CRM_PREVIEW,
        crmSyncId,
        crmSyncSource,
        cancelTokenId,
        sequence: findPanelSequence(),
        shouldForcePopout: true,
        popupAppPanelId,
        mapping,
        externalListId,
      }),
    );
    if (crmSyncResourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
      dispatch(fetchPreviewAccountsAsyncAction(panelId));
    } else if (crmSyncResourceType === CrmSyncIntegrationResourceTypesEnum.CARD) {
      dispatch(fetchPreviewCardsAsyncAction(panelId));
    }
  };
}

function fetchPreviewAccountsAsyncAction(panelId: CrmPreviewPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchPreviewDataAction(panelId));
      const previewPanel = makeGetCrmPreviewPanel()(getState(), {panelId});
      const companyUrl = getCompanyUrl(getState());

      const accounts = await postApiAsync(
        ApiPreviewAccounts,
        `${companyUrl}/syncs/${previewPanel.crmSyncId}/preview`,
        {
          mapping: previewPanel.mapping,
          list_id: previewPanel.externalListId,
        },
      );
      dispatch(fetchPreviewDataCompletedAction(panelId, accounts, []));
    } catch {
      dispatch(fetchPreviewDataFailedAction(panelId));
    }
  };
}

function fetchPreviewCardsAsyncAction(panelId: CrmPreviewPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchPreviewDataAction(panelId));
      const previewPanel = makeGetCrmPreviewPanel()(getState(), {panelId});
      const companyUrl = getCompanyUrl(getState());

      const cards = await postApiAsync(
        ApiPreviewCards,
        `${companyUrl}/syncs/${previewPanel.crmSyncId}/preview`,
        {
          mapping: previewPanel.mapping,
          list_id: previewPanel.externalListId,
        },
      );
      dispatch(fetchPreviewDataCompletedAction(panelId, [], cards));
    } catch {
      dispatch(fetchPreviewDataFailedAction(panelId));
    }
  };
}
